import { useEffect, useRef, useState } from 'react';
import { FiX, FiLoader } from 'react-icons/fi';
import toast from 'react-hot-toast';
import apiClient from '../../../api/apiClient';

function mediaTypeFromUrl(url) {
  const clean = String(url || '').split('?')[0].toLowerCase();
  if (/\.(png|jpe?g|webp|gif)$/i.test(clean)) return 'image';
  if (/\.pdf$/i.test(clean)) return 'pdf';
  return null;
}

function mediaTypeFromBlob(blob, url) {
  const mime = String(blob?.type || '').toLowerCase();
  if (mime.startsWith('image/')) return 'image';
  if (mime === 'application/pdf') return 'pdf';
  // application/octet-stream is common — fall back to URL extension
  return mediaTypeFromUrl(url) || 'image';
}

/**
 * Inline preview modal for a server-hosted CMR/CML document.
 * Fetches the asset with auth credentials so protected upload URLs work.
 */
export default function CmrDocumentPreviewModal({ open, sourceUrl, title = 'CMR / CML document', onClose }) {
  const objectUrlRef = useRef('');
  const [loading, setLoading] = useState(false);
  const [objectUrl, setObjectUrl] = useState('');
  const [mediaType, setMediaType] = useState('image');

  useEffect(() => {
    if (!open || !sourceUrl) return undefined;

    let cancelled = false;

    async function load() {
      setLoading(true);
      setObjectUrl('');
      try {
        const response = await apiClient.get(sourceUrl, {
          responseType: 'blob',
          withCredentials: true,
        });
        if (cancelled) return;
        const blob = response.data;
        const url = URL.createObjectURL(blob);
        objectUrlRef.current = url;
        setObjectUrl(url);
        setMediaType(mediaTypeFromBlob(blob, sourceUrl));
      } catch {
        if (!cancelled) {
          toast.error('Could not load CMR document preview.');
          onClose();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [open, sourceUrl, onClose]);

  // Revoke blob URL on close
  useEffect(() => {
    if (open) return undefined;
    if (objectUrlRef.current) {
      try { URL.revokeObjectURL(objectUrlRef.current); } catch { /* ignore */ }
      objectUrlRef.current = '';
    }
    setObjectUrl('');
    return undefined;
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cmr-preview-title"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative flex max-h-[90vh] w-full max-w-3xl flex-col rounded-xl bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h3 id="cmr-preview-title" className="text-sm font-semibold text-gray-900">
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800 focus:outline-none"
            aria-label="Close preview"
          >
            <FiX className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex min-h-[280px] flex-1 items-center justify-center overflow-auto p-4">
          {loading ? (
            <div className="flex flex-col items-center gap-2 text-gray-400">
              <FiLoader className="h-6 w-6 animate-spin" />
              <p className="text-xs">Loading document…</p>
            </div>
          ) : objectUrl && mediaType === 'pdf' ? (
            <iframe
              title={title}
              src={objectUrl}
              className="h-[70vh] w-full rounded-lg border border-gray-200"
            />
          ) : objectUrl ? (
            <img
              src={objectUrl}
              alt={title}
              className="max-h-[70vh] max-w-full rounded-lg object-contain"
            />
          ) : (
            <p className="text-sm text-gray-500">Preview not available.</p>
          )}
        </div>
      </div>
    </div>
  );
}
